import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { CustomLoading } from "components/CustomLoading";
import { Activity, FileText, MessageSquare, HelpCircle, CheckCircle, Link as LinkIcon, File, Folder as FolderIcon } from "lucide-react";
import { User, displayName, initials, avatarColor, loadUsers } from "./user-display";
import Breadcrumb, { BreadcrumbPill } from "./Breadcrumb";

interface ActivityItem {
    id: string;
    actorId: string;
    actionType: string;
    entityType: string;
    entityId: string;
    entityTitle: string | null;
    categoryId: string | null;
    createdAt: string;
}

interface Category {
    id: string;
    name: string;
    color: string | null;
    icon: string | null;
}

interface Props {
    isAdmin: boolean;
    categories: Category[];
    onSelectPage: (pageId: string, categoryId: string) => void;
    onSelectCategory: (categoryId: string) => void;
}

const PAGE_SIZE = 20;

export default function HomeView({ categories, onSelectPage, onSelectCategory }: Props) {
    const [activities, setActivities] = useState<ActivityItem[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    useEffect(() => {
        Promise.all([
            axios.get(`/api/manuals/activity?skip=0&take=${PAGE_SIZE}`),
            loadUsers(),
        ]).then(([res, us]) => {
            if (res.data.success) {
                setActivities(res.data.items);
                setHasMore(res.data.items.length === PAGE_SIZE);
            }
            setUsers(us);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const usersById = useMemo(() => {
        const m = new Map<string, User>();
        users.forEach(u => m.set(u.id, u));
        return m;
    }, [users]);

    const categoriesById = useMemo(() => {
        const m = new Map<string, Category>();
        categories.forEach(c => m.set(c.id, c));
        return m;
    }, [categories]);

    const loadMore = () => {
        setLoadingMore(true);
        axios.get(`/api/manuals/activity?skip=${activities.length}&take=${PAGE_SIZE}`)
            .then(res => {
                if (res.data.success) {
                    setActivities(prev => [...prev, ...res.data.items]);
                    setHasMore(res.data.items.length === PAGE_SIZE);
                }
                setLoadingMore(false);
            })
            .catch(() => setLoadingMore(false));
    };

    if (loading) return <div className="m-loading"><CustomLoading /></div>;

    const pills: BreadcrumbPill[] = [{ kind: "home", label: "Team Home" }];

    return (
        <div className="m-home-page">
            <Breadcrumb pills={pills} />

            <div className="m-home-body">
                <div className="m-home-section-head">
                    <div className="m-home-section-title">Recent activity</div>
                </div>

                {activities.length === 0 ? (
                    <div className="m-empty-state">
                        <Activity size={32} strokeWidth={1.5} />
                        <div>No activity yet</div>
                        <div className="m-empty-hint">Actions will appear here as your team uses the wiki</div>
                    </div>
                ) : (
                    <>
                        <div className="m-activity-list">
                            {activities.map(a => (
                                <ActivityRow
                                    key={a.id}
                                    item={a}
                                    actor={usersById.get(a.actorId)}
                                    category={a.categoryId ? categoriesById.get(a.categoryId) : undefined}
                                    onSelectPage={onSelectPage}
                                    onSelectCategory={onSelectCategory}
                                />
                            ))}
                        </div>
                        {hasMore && (
                            <div className="m-home-loadmore">
                                <button className="m-btn m-btn-ghost" onClick={loadMore} disabled={loadingMore}>
                                    {loadingMore ? "Loading..." : "Load more"}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

interface RowProps {
    item: ActivityItem;
    actor: User | undefined;
    category: Category | undefined;
    onSelectPage: (pageId: string, categoryId: string) => void;
    onSelectCategory: (categoryId: string) => void;
}

function ActivityRow({ item, actor, category, onSelectPage, onSelectCategory }: RowProps) {
    const actorName = actor ? displayName(actor) : item.actorId.slice(0, 8);
    const actorInitial = initials(actorName);
    const actorColor = avatarColor(item.actorId);
    const verb = verbFor(item.actionType, item.entityType);
    const Icon = iconFor(item.actionType, item.entityType);

    const entityClickable = item.entityType === "page" && item.categoryId;
    const onEntityClick = () => {
        if (entityClickable) onSelectPage(item.entityId, item.categoryId!);
    };

    return (
        <div className="m-activity-row">
            <div className="m-activity-avatar" style={{ background: actorColor }} title={actorName}>
                {actorInitial}
            </div>
            <div className="m-activity-body">
                <span className="m-activity-actor">{actorName}</span>
                {" "}
                <span className="m-activity-verb">{verb}</span>
                {" "}
                {item.entityTitle && (
                    <span
                        className={`m-activity-entity-chip ${entityClickable ? "is-link" : ""}`}
                        onClick={entityClickable ? onEntityClick : undefined}
                        role={entityClickable ? "button" : undefined}
                    >
                        <Icon size={14} strokeWidth={2} />
                        <span className="m-activity-entity-title">{item.entityTitle}</span>
                    </span>
                )}
                {category && (
                    <>
                        {" "}
                        <span className="m-activity-in">in</span>
                        {" "}
                        <span
                            className="m-activity-crumb"
                            onClick={() => onSelectCategory(category.id)}
                            role="button"
                        >
                            {category.icon ? (
                                <span className="m-activity-crumb-dot" style={{ background: category.color || "#94a3b8" }} />
                            ) : (
                                <FolderIcon size={12} strokeWidth={2} color={category.color || "#94a3b8"} />
                            )}
                            <span>{category.name}</span>
                        </span>
                    </>
                )}
            </div>
            <div className="m-activity-time">{formatRelative(item.createdAt)}</div>
        </div>
    );
}

function verbFor(actionType: string, entityType: string): string {
    const a = (actionType || "").toLowerCase();
    const e = (entityType || "").toLowerCase();
    if (a === "commented" || a === "comment_created") return `commented on a ${e || "page"}`;
    if (a === "published" && e === "page") return "published";
    if (a === "created" && e === "page") return "created a page";
    if (a === "created" && e === "question") return "asked";
    if (a === "created" && e === "link") return "added a link";
    if (a === "created" && e === "document") return "uploaded a document";
    if (a === "created" && e === "folder") return "created a folder";
    if (a === "created" && e === "category") return "created a category";
    if (a === "updated") return "updated";
    if (a === "answered") return "answered";
    if (a === "verified") return "verified";
    if (a === "deleted") return "deleted";
    if (a === "restored") return "restored";
    if (a === "pinned") return "pinned";
    if (a === "unpinned") return "unpinned";
    if (a === "moved") return "moved";
    return `${a} a ${e || "item"}`.trim();
}

function iconFor(actionType: string, entityType: string) {
    const a = (actionType || "").toLowerCase();
    const e = (entityType || "").toLowerCase();
    if (a === "commented" || a === "comment_created") return MessageSquare;
    if (e === "question") return HelpCircle;
    if (a === "answered") return CheckCircle;
    if (e === "link") return LinkIcon;
    if (e === "document") return File;
    if (e === "folder") return FolderIcon;
    return FileText;
}

function formatRelative(iso: string): string {
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
}
